'use client';

import { useEffect } from 'react';
import { useThemeTokens } from '@/lib/themeTokens';

/**
 * Bridges the app's active theme → shadcn/ui CSS variables.
 *
 * shadcn primitives read `hsl(var(--primary))` etc. Those variables have
 * hardcoded zinc defaults in globals.css, but the product is white-label: the
 * REAL theme comes from the backend (tenant preset + super-admin Theme
 * Customizer, resolved by ThemedShell) and the light/dark toggle. This component
 * reads the already-resolved MUI tokens (useThemeTokens is preset-, custom-theme-
 * and mode-aware) and writes them onto <html> as the shadcn variables, so every
 * migrated shadcn page follows the same theme as the MUI pages — no second source
 * of truth. See [[project_theme_customizer]] / [[project_theme_preset_system]].
 *
 * Rendered inside the MUI ThemeProvider (so the hook has context). Inline styles
 * on documentElement win over both `:root` and `.dark` rules, and re-run whenever
 * the theme object changes.
 */

/** Parse #hex / rgb() / rgba() → the "H S% L%" triplet shadcn expects (alpha dropped). */
function toHslTriplet(input: string): string | null {
  if (!input) return null;
  const s = input.trim();
  let r = 0, g = 0, b = 0;
  if (s.startsWith('#')) {
    let hex = s.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    if (hex.length !== 6) return null;
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const m = s.match(/rgba?\(([^)]+)\)/i);
    if (!m) return null;
    const p = m[1].split(',').map((x) => parseFloat(x));
    [r, g, b] = p;
    if (r == null || g == null || b == null) return null;
  }
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0, sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

/** Perceived luminance (0–1) of a colour, for picking a readable foreground. */
function luminance(input: string): number {
  const s = input.trim();
  let r = 0, g = 0, b = 0;
  if (s.startsWith('#')) {
    let hex = s.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    const m = s.match(/rgba?\(([^)]+)\)/i);
    if (!m) return 0;
    const p = m[1].split(',').map((x) => parseFloat(x));
    [r, g, b] = p;
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

export function ShadcnThemeSync() {
  const t = useThemeTokens();

  useEffect(() => {
    const root = document.documentElement;
    const set = (k: string, v: string | null) => { if (v) root.style.setProperty(k, v); };

    // Brand primary comes from the theme accent; its foreground flips for contrast.
    const primaryFg = luminance(t.ACCENT) > 0.6 ? '240 6% 10%' : '0 0% 98%';

    set('--background', toHslTriplet(t.BG));
    set('--foreground', toHslTriplet(t.TEXT));
    set('--card', toHslTriplet(t.PAPER));
    set('--card-foreground', toHslTriplet(t.TEXT));
    set('--popover', toHslTriplet(t.PAPER));
    set('--popover-foreground', toHslTriplet(t.TEXT));
    set('--primary', toHslTriplet(t.ACCENT));
    set('--primary-foreground', primaryFg);
    set('--secondary', toHslTriplet(t.SURFACE_SOFT));
    set('--secondary-foreground', toHslTriplet(t.TEXT));
    set('--muted', toHslTriplet(t.SURFACE_SOFT));
    set('--muted-foreground', toHslTriplet(t.MUTED));
    set('--accent', toHslTriplet(t.SURFACE_SOFT));
    set('--accent-foreground', toHslTriplet(t.TEXT));
    set('--border', toHslTriplet(t.BORDER));
    set('--input', toHslTriplet(t.BORDER));
    set('--ring', toHslTriplet(t.ACCENT));
    set('--success', toHslTriplet(t.GREEN));
    set('--warning', toHslTriplet(t.AMBER));
    set('--destructive', toHslTriplet(t.RED));
  }, [t]);

  return null;
}
